import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, ChevronLeft, Upload, Check, Plus, X, ChevronDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { useBrand } from '@/context/BrandContext';
import { ImageUploadModal } from '../image-upload';
import { colorPresetService } from '@/lib/colorPresetService';
import { typographyPresetService, TypographyPreset } from '@/lib/typographyPresetService';
import { calculateTypeScale } from '@/lib/typographyScaleUtils';
import { ColorPreset } from '../color-presets/types';
import { useWorkspace } from '@/context/WorkspaceContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';

const steps = [
  { id: 1, title: 'Basic Information', subtitle: 'Start with the essentials' },
  { id: 2, title: 'Brand Story & Visuals', subtitle: 'Share your brand journey' },
  { id: 3, title: 'Logo & Branding Assets', subtitle: 'Upload your brand identity' },
  { id: 4, title: 'Color & Typography', subtitle: 'Define your design system' },
  { id: 5, title: 'Brand Mood & Moodboard', subtitle: 'Define your brand personality' },
  { id: 6, title: 'Contact & Social', subtitle: 'Connect with your audience' }
];

const categories = [
  { value: 'technology', label: 'Technology', subcategories: ['SaaS', 'Mobile Apps', 'AI/ML', 'Cloud Services', 'Cybersecurity', 'Hardware'] },
  { value: 'healthcare', label: 'Healthcare', subcategories: ['Medical Devices', 'Telemedicine', 'Pharmaceuticals', 'Wellness', 'Mental Health'] },
  { value: 'finance', label: 'Finance', subcategories: ['Banking', 'Investment', 'Insurance', 'Fintech', 'Cryptocurrency'] },
  { value: 'education', label: 'Education', subcategories: ['E-Learning', 'Training', 'Universities', 'K-12', 'Corporate Learning'] },
  { value: 'retail', label: 'Retail', subcategories: ['Fashion', 'Electronics', 'Home Goods', 'Groceries', 'Specialty Stores'] },
  { value: 'fashion', label: 'Fashion', subcategories: ['Apparel', 'Accessories', 'Footwear', 'Jewelry', 'Luxury'] },
  { value: 'food-beverage', label: 'Food & Beverage', subcategories: ['Restaurants', 'Cafes', 'Food Delivery', 'Catering', 'Beverages'] },
  { value: 'travel', label: 'Travel', subcategories: ['Hotels', 'Airlines', 'Tours', 'Booking Platforms', 'Travel Services'] },
  { value: 'real-estate', label: 'Real Estate', subcategories: ['Residential', 'Commercial', 'Property Management', 'Real Estate Tech'] },
  { value: 'consulting', label: 'Consulting', subcategories: ['Management', 'IT', 'Marketing', 'HR', 'Strategy'] }
];

const brandMoods = [
  'Professional', 'Playful', 'Elegant', 'Bold', 'Minimal', 'Vintage',
  'Modern', 'Luxurious', 'Friendly', 'Innovative', 'Trustworthy', 'Energetic',
  'Calm', 'Sophisticated', 'Fun', 'Serious', 'Creative', 'Traditional'
];

interface OnboardingFlowProps {
  isGlobalDarkMode: boolean;
}

type ImageField = 'introImage' | 'storyImage' | 'missionVisionImage' | 'coverImage' | 'logo' | 'favicon' | 'moodboard';

interface SocialLink {
  platform: string;
  url: string;
}

export const OnboardingFlow: React.FC<OnboardingFlowProps> = ({ isGlobalDarkMode }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [showImageModal, setShowImageModal] = useState(false);
  const [currentImageField, setCurrentImageField] = useState<ImageField | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    subcategory: '',
    website: '',
    introImage: '',
    introDescription: '',
    storyImage: '',
    storyDescription: '',
    missionVisionImage: '',
    missionDescription: '',
    visionDescription: '',
    coverImage: '',
    logo: '',
    logoDetails: '',
    favicon: '',
    iconDetails: '',
    selectedColorPreset: null as ColorPreset | null,
    primaryColor: '#3B82F6',
    secondaryColor: '',
    accentColor: '',
    bgColor: '#FFFFFF',
    titleColor: '#000000',
    textColor: '',
    selectedTypographyPreset: null as TypographyPreset | null,
    headingFont: 'Inter',
    bodyFont: 'Inter',
    h1Size: '48',
    h2Size: '36',
    h3Size: '30',
    h4Size: '24',
    h5Size: '20',
    bodySize: '16',
    brandMoods: [] as string[],
    moodboardImages: [] as string[],
    targetAudience: [] as string[],
    contactPhone: '',
    contactEmail: '',
    contactAddress: '',
    socialLinks: [] as SocialLink[]
  });

  const navigate = useNavigate();
  const { dispatch } = useBrand();
  const { currentWorkspace } = useWorkspace();

  const [colorPresets, setColorPresets] = useState<ColorPreset[]>([]);
  const [isLoadingColorPresets, setIsLoadingColorPresets] = useState(true);
  const [typographyPresets, setTypographyPresets] = useState<TypographyPreset[]>([]);
  const [isLoadingTypographyPresets, setIsLoadingTypographyPresets] = useState(true);

  useEffect(() => {
    const loadColorPresets = async () => {
      if (!currentWorkspace) return;
      setIsLoadingColorPresets(true);
      try {
        const presets = await colorPresetService.getPresetsByWorkspace(currentWorkspace.id);
        setColorPresets(presets);
      } catch (error) {
        console.error('Failed to load color presets:', error);
        toast.error('Failed to load color presets');
        setColorPresets([]);
      } finally {
        setIsLoadingColorPresets(false);
      }
    };
    loadColorPresets();
  }, [currentWorkspace]);

  useEffect(() => {
    const loadTypographyPresets = async () => {
      if (!currentWorkspace) return;
      setIsLoadingTypographyPresets(true);
      try {
        const presets = await typographyPresetService.getPresetsByWorkspace(currentWorkspace.id);
        setTypographyPresets(presets);
      } catch (error) {
        console.error('Failed to load typography presets:', error);
        toast.error('Failed to load typography presets');
      } finally {
        setIsLoadingTypographyPresets(false);
      }
    };
    loadTypographyPresets();
  }, [currentWorkspace]);

  const handleNext = () => {
    if (currentStep < steps.length - 1 && canProceed()) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleImageUpload = (field: ImageField) => {
    setCurrentImageField(field);
    setShowImageModal(true);
  };

  const handleImageSelect = (imageUrl: string) => {
    if (currentImageField) {
      if (currentImageField === 'moodboard') {
        setFormData(prev => ({
          ...prev,
          moodboardImages: [...prev.moodboardImages, imageUrl]
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          [currentImageField]: imageUrl
        }));
      }
    }
    setShowImageModal(false);
    setCurrentImageField(null);
  };

  const addSocialLink = () => {
    setFormData(prev => ({
      ...prev,
      socialLinks: [...prev.socialLinks, { platform: 'facebook', url: '' }]
    }));
  };

  const updateSocialLink = (index: number, field: 'platform' | 'url', value: string) => {
    setFormData(prev => ({
      ...prev,
      socialLinks: prev.socialLinks.map((link, i) =>
        i === index ? { ...link, [field]: value } : link
      )
    }));
  };

  const removeSocialLink = (index: number) => {
    setFormData(prev => ({
      ...prev,
      socialLinks: prev.socialLinks.filter((_, i) => i !== index)
    }));
  };

  const canProceed = () => {
    switch (currentStep) {
      case 0:
        return formData.name.trim() !== '' && formData.category !== '' && formData.targetAudience.length >= 3;
      case 1:
        return formData.introDescription || formData.storyDescription || formData.missionDescription || formData.visionDescription ||
               formData.introImage || formData.storyImage || formData.missionVisionImage;
      case 2:
        return formData.logo !== '' && formData.coverImage !== '' && formData.favicon !== '';
      case 3:
        return formData.selectedColorPreset !== null && formData.primaryColor && formData.bgColor && formData.titleColor &&
               formData.selectedTypographyPreset !== null && formData.headingFont && formData.bodyFont;
      case 4:
        return formData.moodboardImages.length >= 5;
      default:
        return true;
    }
  };

  const handleGenerate = () => {
    dispatch({ type: 'SET_CURRENT_BRAND_ID', payload: null });

    const brandData = {
      name: formData.name,
      brandUrl: formData.website,
      brandLogo: formData.logo,
      primaryDarkLogo: '',
      brandDescription: formData.introDescription,
      brandImage: formData.introImage,
      brandStoryDescription: formData.storyDescription,
      brandStoryImage: formData.storyImage,
      industry: formData.category,
      personality: [],
      colors: {
        primary: formData.primaryColor,
        secondary: formData.secondaryColor || '',
        accent: formData.accentColor || '',
        background: formData.bgColor,
        title: formData.titleColor,
        text: formData.textColor || ''
      },
      typography: {
        primary: formData.selectedTypographyPreset?.heading_font_family || formData.headingFont || 'Inter',
        secondary: formData.selectedTypographyPreset?.body_font_family || formData.bodyFont || 'Inter',
      },
      selectedColorPreset: formData.selectedColorPreset,
      selectedTypographyPreset: formData.selectedTypographyPreset,
      globalTitleFont: formData.selectedTypographyPreset?.heading_font_family || formData.headingFont || 'Inter',
      globalTextFont: formData.selectedTypographyPreset?.body_font_family || formData.bodyFont || 'Inter',
      mission: formData.missionDescription,
      vision: formData.visionDescription,
      values: [],
      targetAudience: formData.targetAudience,
      moodboardImages: formData.moodboardImages,
      missionVisionImage: formData.missionVisionImage,
      coverImage: formData.coverImage,
      brandThumbnail: formData.coverImage,
      favicon: formData.favicon,
      logoDetails: formData.logoDetails,
      iconDetails: formData.iconDetails,
      category: formData.category,
      subcategory: formData.subcategory,
      contactDetails: {
        phone: formData.contactPhone,
        email: formData.contactEmail,
        address: formData.contactAddress,
        socialLinks: formData.socialLinks
      }
    };

    dispatch({ type: 'SET_BRAND_DATA', payload: brandData });
    const sections = generateBrandSections(brandData);
    dispatch({ type: 'SET_SECTIONS', payload: sections });
    navigate('/preview');
  };

  const getCurrentDate = () => {
    const now = new Date();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const year = now.getFullYear();
    return `${month}-${day}-${year}`;
  };

  const generateBrandSections = (data: any): Section[] => {
    const primaryColor = data.colors.primary;
    const secondaryColor = data.colors.secondary || primaryColor;
    const accentColor = data.colors.accent || primaryColor;

    const createCoverSection = (id: string, title: string, coverTitle: string, colorType: 'primary' | 'secondary' | 'accent' = 'primary') => {
      const backgroundColor = colorType === 'primary' ? primaryColor : colorType === 'secondary' ? secondaryColor : accentColor;

      return {
        id,
        title,
        content: {
          coverTitle,
          publishedDate: 'Published Date: {{publishedDate}}',
          brandGuideline: '{{brandName}}/Brand Guideline',
          designCredit: 'Designed By {{designCredit}}',
          brandLogo: data.brandLogo || ''
        },
        styles: {
          backgroundColor: backgroundColor,
          textColor: '#FFFFFF',
          fontFamily: data.selectedTypographyPreset?.heading_font_family || 'Helvetica',
          fontSize: '48px',
          padding: '60px',
          position: 'relative',
          titleFontFamily: data.selectedTypographyPreset?.heading_font_family || 'Helvetica'
        },
        customization: {
          backgroundType: 'color',
          backgroundColor: backgroundColor,
          backgroundImage: '',
          backgroundPosition: 'center',
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
          overlayEnabled: false,
          overlayColor: '#000000',
          overlayOpacity: 0,
          overlayBlendMode: 'normal'
        }
      };
    };

    return [
      // 1. Guidelines Cover
      {
        id: 'guidelines',
        title: 'Brand Style Guidelines',
        content: {
          brandBookTitle: '{{brandName}}',
          currentDate: `Date: ${getCurrentDate()}`,
          brandGuideline: '{{brandName}}/Brand Guideline',
          designCredit: 'Designed by {{designCredit}}',
          brandName: data.name || 'Brand Name',
          brandLogo: data.brandLogo || '',
          brandUrl: data.brandUrl || '',
          currentDateStyles: {
            textAlign: 'right',
            fontFamily: data.selectedTypographyPreset?.body_font_family || 'Helvetica',
            color: '#FFFFFF'
          },
          brandBookTitleStyles: {
            textAlign: 'center',
            fontFamily: data.selectedTypographyPreset?.heading_font_family || 'Helvetica',
            textTransform: 'uppercase',
            lineHeight: 1.0,
            fontWeight: 'bold',
            color: '#FFFFFF'
          },
          designCreditStyles: {
            textAlign: 'right',
            fontFamily: data.selectedTypographyPreset?.body_font_family || 'Helvetica',
            color: '#FFFFFF'
          },
          brandGuidelineStyles: {
            fontFamily: data.selectedTypographyPreset?.body_font_family || 'Helvetica',
            color: '#FFFFFF'
          }
        },
        styles: {
          backgroundColor: primaryColor,
          textColor: '#FFFFFF',
          fontFamily: data.selectedTypographyPreset?.heading_font_family || 'Helvetica',
          fontSize: '48px',
          padding: '60px',
          position: 'relative'
        },
        customization: {
          backgroundType: 'color',
          backgroundColor: primaryColor,
          backgroundImage: '',
          backgroundPosition: 'center',
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
          overlayEnabled: false,
          overlayColor: '#000000',
          overlayOpacity: 0,
          overlayBlendMode: 'normal'
        }
      },
      // 2. Table of Contents
      {
        id: 'table-of-contents',
        title: 'Table of Contents',
        content: {
          tocImage: data.coverImage || '/img-placeholder-16-9.png',
          listTitle: 'CONTENTS',
          item1Title: 'Introduction',
          item1Number: '01',
          item2Title: 'Logo',
          item2Number: '02',
          item3Title: 'Color',
          item3Number: '03',
          item4Title: 'Typography',
          item4Number: '04',
          item5Title: 'Moodboard',
          item5Number: '05',
          item6Title: 'Thank You',
          item6Number: '06'
        },
        styles: {
          backgroundColor: '#FFFFFF',
          textColor: '#333942',
          fontFamily: data.selectedTypographyPreset?.body_font_family || 'Helvetica',
          fontSize: '16px',
          padding: '60px'
        },
        customization: {
          backgroundType: 'color',
          backgroundColor: '#FFFFFF',
          backgroundImage: '',
          backgroundPosition: 'center',
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
          overlayEnabled: false,
          overlayColor: '#000000',
          overlayOpacity: 0,
          overlayBlendMode: 'normal'
        }
      },
      // 3. Introduction
      {
        id: 'introduction',
        title: 'Brand Introduction',
        content: {
          introTitle: 'Brand Introduction',
          brandDescription: data.brandDescription || '',
          brandImage: data.brandImage || '/img-placeholder-16-9.png'
        },
        styles: {
          backgroundColor: '#FFFFFF',
          textColor: '#333942',
          fontFamily: data.selectedTypographyPreset?.body_font_family || 'Helvetica',
          fontSize: '16px',
          padding: '60px',
          titleFontFamily: data.selectedTypographyPreset?.heading_font_family || 'Helvetica'
        },
        customization: {
          backgroundType: 'color',
          backgroundColor: '#FFFFFF',
          backgroundImage: '',
          backgroundPosition: 'center',
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
          overlayEnabled: false,
          overlayColor: '#000000',
          overlayOpacity: 0,
          overlayBlendMode: 'normal'
        }
      },
      // 4. Brand Story
      {
        id: 'brand-story',
        title: 'Brand Story',
        content: {
          storyTitle: 'Brand Story',
          brandStoryDescription: data.brandStoryDescription || '',
          storyImage: data.brandStoryImage || '/img-placeholder-16-9.png'
        },
        styles: {
          backgroundColor: '#FFFFFF',
          textColor: '#333942',
          fontFamily: data.selectedTypographyPreset?.body_font_family || 'Helvetica',
          fontSize: '16px',
          padding: '60px',
          titleFontFamily: data.selectedTypographyPreset?.heading_font_family || 'Helvetica'
        },
        customization: {
          backgroundType: 'color',
          backgroundColor: '#FFFFFF',
          backgroundImage: '',
          backgroundPosition: 'center',
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
          overlayEnabled: false,
          overlayColor: '#000000',
          overlayOpacity: 0,
          overlayBlendMode: 'normal'
        }
      },
      // 5. Mission & Vision
      {
        id: 'mission-vision',
        title: 'Mission & Vision',
        content: {
          missionTitle: 'Our Mission',
          mission: data.mission || '',
          visionTitle: 'Our Vision',
          vision: data.vision || '',
          missionVisionImage: data.missionVisionImage || '/img-placeholder-16-9.png'
        },
        styles: {
          backgroundColor: '#FFFFFF',
          textColor: '#333942',
          fontFamily: data.selectedTypographyPreset?.body_font_family || 'Helvetica',
          fontSize: '16px',
          padding: '60px',
          titleFontFamily: data.selectedTypographyPreset?.heading_font_family || 'Helvetica'
        },
        customization: {
          backgroundType: 'color',
          backgroundColor: '#FFFFFF',
          backgroundImage: '',
          backgroundPosition: 'center',
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
          overlayEnabled: false,
          overlayColor: '#000000',
          overlayOpacity: 0,
          overlayBlendMode: 'normal'
        }
      },
      // 6. Target Audience
      {
        id: 'target-audience',
        title: 'Target Audience',
        content: {
          sectionTitle: 'Target Audience',
          description: 'Define the key demographic groups and personas that your brand aims to reach and engage with.',
          audiences: JSON.stringify(data.targetAudience && data.targetAudience.length > 0 ? data.targetAudience : ['Young Professionals', 'Students', 'Entrepreneurs', 'Creatives'])
        },
        styles: {
          backgroundColor: '#FFFFFF',
          textColor: '#333942',
          fontFamily: data.selectedTypographyPreset?.body_font_family || 'Helvetica',
          fontSize: '16px',
          padding: '60px',
          titleFontFamily: data.selectedTypographyPreset?.heading_font_family || 'Helvetica'
        },
        customization: {
          backgroundType: 'color',
          backgroundColor: '#FFFFFF',
          backgroundImage: '',
          backgroundPosition: 'center',
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
          overlayEnabled: false,
          overlayColor: '#000000',
          overlayOpacity: 0,
          overlayBlendMode: 'normal'
        }
      },
      // 7. Brand Logo Cover
      createCoverSection('brand-logo-cover', 'Brand Logo Cover', 'LOGO', 'primary'),
      // 8. Brand Logo
      {
        id: 'brand-logo',
        title: 'Brand Logo',
        content: {
          logoTitle: 'Brand Logo',
          logoDescription: data.logoDetails || 'brand\'s logo represents our brand identity and values.',
          primaryLogo: data.brandLogo || '',
          originalLogo: data.brandLogo || '',
          lightLogo: data.brandLogo || '',
          darkLogo: data.brandLogo || ''
        },
        styles: {
          backgroundColor: '#FFFFFF',
          textColor: '#333942',
          fontFamily: data.selectedTypographyPreset?.body_font_family || 'Helvetica',
          fontSize: '16px',
          padding: '60px',
          titleFontFamily: data.selectedTypographyPreset?.heading_font_family || 'Helvetica'
        },
        customization: {
          backgroundType: 'color',
          backgroundColor: '#FFFFFF',
          backgroundImage: '',
          backgroundPosition: 'center',
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
          overlayEnabled: false,
          overlayColor: '#000000',
          overlayOpacity: 0,
          overlayBlendMode: 'normal'
        }
      },
      // 9. Favicon
      {
        id: 'brand-favicon',
        title: 'Brand Favicon',
        content: {
          faviconTitle: 'Favicon',
          faviconDescription: data.iconDetails || 'Favicon represents our brand in browser tabs and bookmarks.',
          primaryFavicon: data.favicon || '',
          originalFavicon: data.favicon || '',
          lightFavicon: data.favicon || '',
          darkFavicon: data.favicon || ''
        },
        styles: {
          backgroundColor: '#FFFFFF',
          textColor: '#333942',
          fontFamily: data.selectedTypographyPreset?.body_font_family || 'Helvetica',
          fontSize: '16px',
          padding: '60px',
          titleFontFamily: data.selectedTypographyPreset?.heading_font_family || 'Helvetica'
        },
        customization: {
          backgroundType: 'color',
          backgroundColor: '#FFFFFF',
          backgroundImage: '',
          backgroundPosition: 'center',
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
          overlayEnabled: false,
          overlayColor: '#000000',
          overlayOpacity: 0,
          overlayBlendMode: 'normal'
        }
      },
      // 10. Do & Don't
      {
        id: 'do-dont',
        title: 'Logo Usage Guidelines',
        content: {
          mainTitle: 'Logo Usage',
          usageDescription: 'These examples demonstrate common mistakes to avoid when using the logo. Following these guidelines ensures consistent brand representation.',
          usage1: data.brandLogo || '/img-placeholder-16-9.png',
          usage1Label: 'Best Practice 1',
          usage2: data.brandLogo || '/img-placeholder-16-9.png',
          usage2Label: 'Best Practice 2',
          usage3: data.brandLogo || '/img-placeholder-16-9.png',
          usage3Label: 'Best Practice 3',
          usage4: data.brandLogo || '/img-placeholder-16-9.png',
          usage4Label: 'Best Practice 4',
          usage5: data.brandLogo || '/img-placeholder-16-9.png',
          usage5Label: 'Best Practice 5',
          usage6: data.brandLogo || '/img-placeholder-16-9.png',
          usage6Label: 'Best Practice 6'
        },
        styles: {
          backgroundColor: '#FFFFFF',
          textColor: '#333942',
          fontFamily: data.selectedTypographyPreset?.body_font_family || 'Helvetica',
          fontSize: '16px',
          padding: '60px',
          titleFontFamily: data.selectedTypographyPreset?.heading_font_family || 'Helvetica'
        },
        customization: {
          backgroundType: 'color',
          backgroundColor: '#FFFFFF',
          backgroundImage: '',
          backgroundPosition: 'center',
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
          overlayEnabled: false,
          overlayColor: '#000000',
          overlayOpacity: 0,
          overlayBlendMode: 'normal'
        }
      },
      // 11. Color Cover
      createCoverSection('color-cover', 'Color Cover', 'COLOR', 'secondary'),
      // 12. Brand Colors
      {
        id: 'brand-colors',
        title: 'Brand Colors',
        content: {
          colorsTitle: 'Brand Colors',
          colorsDescription: 'Our carefully curated color palette reflects our brand identity and creates a cohesive visual experience.',
          brandGuideline: '{{brandName}}/Brand Guideline',
          publishedDate: 'Published Date: {{publishedDate}}',
          brandUrl: 'visit: {{brandUrl}}',
          brandName: data.name || 'Brand Name'
        },
        styles: {
          backgroundColor: '#F8F9FA',
          textColor: '#333942',
          fontFamily: data.selectedTypographyPreset?.body_font_family || 'Helvetica',
          fontSize: '18px',
          padding: '60px',
          position: 'relative',
          titleFontFamily: data.selectedTypographyPreset?.heading_font_family || 'Helvetica'
        },
        customization: {
          backgroundType: 'color',
          backgroundColor: '#F8F9FA',
          backgroundImage: '',
          backgroundPosition: 'center',
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
          overlayEnabled: false,
          overlayColor: '#000000',
          overlayOpacity: 0,
          overlayBlendMode: 'normal'
        }
      },
      // 13. Color Palettes
      {
        id: 'color-palettes',
        title: 'Color Palettes',
        content: {
          paletteTitle: 'Color Palette',
          paletteDescription: 'Our color palette includes carefully crafted shades and tints for each brand color, providing flexibility for various design applications.',
          brandGuideline: '{{brandName}}/Brand Guideline',
          publishedDate: 'Published Date: {{publishedDate}}',
          brandUrl: 'visit: {{brandUrl}}',
          brandName: data.name || 'Brand Name'
        },
        styles: {
          backgroundColor: '#F8F9FA',
          textColor: '#333942',
          fontFamily: data.selectedTypographyPreset?.body_font_family || 'Helvetica',
          fontSize: '18px',
          padding: '60px',
          position: 'relative',
          titleFontFamily: data.selectedTypographyPreset?.heading_font_family || 'Helvetica'
        },
        customization: {
          backgroundType: 'color',
          backgroundColor: '#F8F9FA',
          backgroundImage: '',
          backgroundPosition: 'center',
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
          overlayEnabled: false,
          overlayColor: '#000000',
          overlayOpacity: 0,
          overlayBlendMode: 'normal'
        }
      },
      // 14. Typography Cover
      createCoverSection('typography-cover', 'Typography Cover', 'TYPOGRAPHY', 'accent'),
      // 15. Typography Fonts
      {
        id: 'typography-fonts',
        title: 'Typography Fonts',
        content: {
          headingTitle: 'Heading Font',
          headingDescription: 'Use the heading font for page titles, section headers, and important headlines. It establishes visual hierarchy and captures attention with its distinctive character.',
          headingWeights: 'Font Weights: 400, 500, 700.',
          bodyTitle: 'Body Font',
          bodyDescription: 'Apply the body font for paragraphs, descriptions, captions, and longer text blocks. It ensures optimal readability and maintains consistency across all content.',
          bodyWeights: 'Font Weights: 400, 500, 700.'
        },
        styles: {
          backgroundColor: '#FFFFFF',
          textColor: '#000000',
          fontFamily: data.selectedTypographyPreset?.body_font_family || 'Helvetica',
          fontSize: '16px',
          padding: '60px',
          position: 'relative'
        },
        customization: {
          backgroundType: 'color',
          backgroundColor: '#FFFFFF',
          backgroundImage: '',
          backgroundPosition: 'center',
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
          overlayEnabled: false,
          overlayColor: '#000000',
          overlayOpacity: 0,
          overlayBlendMode: 'normal'
        }
      },
      // 16. Photography Cover
      createCoverSection('photography-cover', 'Moodboard Cover', 'MOODBOARD', 'primary'),
      // 17. Moodboard Gallery
      {
        id: 'moodboard-gallery',
        title: 'Moodboard Gallery',
        content: {
          galleryTitle: 'Moodboard',
          galleryImages: JSON.stringify(data.moodboardImages || [])
        },
        styles: {
          backgroundColor: '#FFFFFF',
          textColor: '#333942',
          fontFamily: data.selectedTypographyPreset?.body_font_family || 'Helvetica',
          fontSize: '16px',
          padding: '60px',
          titleFontFamily: data.selectedTypographyPreset?.heading_font_family || 'Helvetica'
        },
        customization: {
          backgroundType: 'color',
          backgroundColor: '#FFFFFF',
          backgroundImage: '',
          backgroundPosition: 'center',
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
          overlayEnabled: false,
          overlayColor: '#000000',
          overlayOpacity: 0,
          overlayBlendMode: 'normal'
        }
      },
      // 18. Thank You Cover
      createCoverSection('thank-you-cover', 'Thank You Cover', 'THANK YOU', 'secondary')
    ];
  };

  const subcategories = categories.find(cat => cat.value === formData.category)?.subcategories || [];

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="brandName" className={isGlobalDarkMode ? 'text-gray-200' : 'text-gray-700'}>
                Brand Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="brandName"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter your brand name"
                className={isGlobalDarkMode ? 'bg-zinc-800 border-zinc-700 text-white' : ''}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="category" className={isGlobalDarkMode ? 'text-gray-200' : 'text-gray-700'}>
                  Category <span className="text-red-500">*</span>
                </Label>
                <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value, subcategory: '' })}>
                  <SelectTrigger id="category" className={isGlobalDarkMode ? 'bg-zinc-800 border-zinc-700 text-white' : ''}>
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="subcategory" className={isGlobalDarkMode ? 'text-gray-200' : 'text-gray-700'}>
                  Subcategory
                </Label>
                <Select
                  value={formData.subcategory}
                  onValueChange={(value) => setFormData({ ...formData, subcategory: value })}
                  disabled={!formData.category || subcategories.length === 0}
                >
                  <SelectTrigger id="subcategory" className={isGlobalDarkMode ? 'bg-zinc-800 border-zinc-700 text-white' : ''}>
                    <SelectValue placeholder="Select a subcategory" />
                  </SelectTrigger>
                  <SelectContent>
                    {subcategories.map(sub => (
                      <SelectItem key={sub} value={sub.toLowerCase().replace(/\s+/g, '-')}>{sub}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="website" className={isGlobalDarkMode ? 'text-gray-200' : 'text-gray-700'}>
                Brand Website <span className="text-gray-400 text-sm">(optional)</span>
              </Label>
              <Input
                id="website"
                type="url"
                value={formData.website}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                placeholder="https://yourbrand.com"
                className={isGlobalDarkMode ? 'bg-zinc-800 border-zinc-700 text-white' : ''}
              />
            </div>

            <div className="space-y-3">
              <Label className={isGlobalDarkMode ? 'text-gray-200' : 'text-gray-700'}>
                Target Audience <span className="text-red-500">*</span> <span className="text-gray-400 text-sm">(Press Enter to add, minimum 3 required)</span>
              </Label>
              <div className="space-y-2">
                <Input
                  placeholder="e.g., Young professionals, Parents, Tech enthusiasts"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const value = e.currentTarget.value.trim();
                      if (value && !formData.targetAudience.includes(value)) {
                        setFormData({
                          ...formData,
                          targetAudience: [...formData.targetAudience, value]
                        });
                        e.currentTarget.value = '';
                      }
                    }
                  }}
                  className={isGlobalDarkMode ? 'bg-zinc-800 border-zinc-700 text-white' : ''}
                />
                <div className="flex flex-wrap gap-2">
                  {formData.targetAudience.map((audience, index) => (
                    <div
                      key={index}
                      className={`px-3 py-1 rounded-full text-sm flex items-center gap-2 ${
                        isGlobalDarkMode
                          ? 'bg-zinc-800 text-gray-300 border border-zinc-700'
                          : 'bg-gray-100 text-gray-700 border border-gray-300'
                      }`}
                    >
                      {audience}
                      <button
                        type="button"
                        onClick={() => {
                          setFormData({
                            ...formData,
                            targetAudience: formData.targetAudience.filter((_, i) => i !== index)
                          });
                        }}
                        className="hover:text-red-500"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
                {formData.targetAudience.length < 3 && (
                  <p className={`text-sm ${isGlobalDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {formData.targetAudience.length === 0
                      ? 'Please add at least 3 target audience tags'
                      : `Add ${3 - formData.targetAudience.length} more tag${3 - formData.targetAudience.length > 1 ? 's' : ''}`
                    }
                  </p>
                )}
              </div>
            </div>
          </div>
        );

      case 1:
        return (
          <div className="space-y-5">
            <div className="space-y-2">
              <Label className={isGlobalDarkMode ? 'text-gray-200' : 'text-gray-700'}>Intro Description</Label>
              <textarea
                value={formData.introDescription}
                onChange={(e) => setFormData({ ...formData, introDescription: e.target.value })}
                placeholder="Describe your brand introduction..."
                rows={4}
                className={`w-full px-4 py-3 border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  isGlobalDarkMode
                    ? 'bg-zinc-800 border-zinc-700 text-white placeholder-gray-400'
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                }`}
              />
            </div>

            <div className="space-y-2">
              <Label className={isGlobalDarkMode ? 'text-gray-200' : 'text-gray-700'}>Story Description</Label>
              <textarea
                value={formData.storyDescription}
                onChange={(e) => setFormData({ ...formData, storyDescription: e.target.value })}
                placeholder="Tell your brand's story..."
                rows={4}
                className={`w-full px-4 py-3 border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  isGlobalDarkMode
                    ? 'bg-zinc-800 border-zinc-700 text-white placeholder-gray-400'
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                }`}
              />
            </div>

            <div className="space-y-2">
              <Label className={isGlobalDarkMode ? 'text-gray-200' : 'text-gray-700'}>Mission Description</Label>
              <textarea
                value={formData.missionDescription}
                onChange={(e) => setFormData({ ...formData, missionDescription: e.target.value })}
                placeholder="Describe your mission..."
                rows={3}
                className={`w-full px-4 py-3 border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  isGlobalDarkMode
                    ? 'bg-zinc-800 border-zinc-700 text-white placeholder-gray-400'
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                }`}
              />
            </div>

            <div className="space-y-2">
              <Label className={isGlobalDarkMode ? 'text-gray-200' : 'text-gray-700'}>Vision Description</Label>
              <textarea
                value={formData.visionDescription}
                onChange={(e) => setFormData({ ...formData, visionDescription: e.target.value })}
                placeholder="Describe your vision..."
                rows={3}
                className={`w-full px-4 py-3 border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  isGlobalDarkMode
                    ? 'bg-zinc-800 border-zinc-700 text-white placeholder-gray-400'
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                }`}
              />
            </div>

            <div className="border-t pt-5" style={{ borderColor: isGlobalDarkMode ? '#374151' : '#e5e7eb' }}>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className={isGlobalDarkMode ? 'text-gray-200' : 'text-gray-700'}>Intro Image</Label>
                  <button
                    onClick={() => handleImageUpload('introImage')}
                    className={`w-full h-32 border-2 border-dashed rounded-lg transition-colors flex flex-col items-center justify-center ${
                      isGlobalDarkMode
                        ? 'border-zinc-700 hover:border-zinc-600 bg-zinc-800/50'
                        : 'border-gray-300 hover:border-gray-400 bg-gray-50'
                    }`}
                  >
                    {formData.introImage ? (
                      <img src={formData.introImage} alt="Intro" className="h-full w-full object-cover rounded" />
                    ) : (
                      <>
                        <Upload className={`w-8 h-8 mb-2 ${isGlobalDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                        <span className={`text-sm ${isGlobalDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Upload Intro Image</span>
                      </>
                    )}
                  </button>
                </div>

                <div className="space-y-2">
                  <Label className={isGlobalDarkMode ? 'text-gray-200' : 'text-gray-700'}>Story Image</Label>
                  <button
                    onClick={() => handleImageUpload('storyImage')}
                    className={`w-full h-32 border-2 border-dashed rounded-lg transition-colors flex flex-col items-center justify-center ${
                      isGlobalDarkMode
                        ? 'border-zinc-700 hover:border-zinc-600 bg-zinc-800/50'
                        : 'border-gray-300 hover:border-gray-400 bg-gray-50'
                    }`}
                  >
                    {formData.storyImage ? (
                      <img src={formData.storyImage} alt="Story" className="h-full w-full object-cover rounded" />
                    ) : (
                      <>
                        <Upload className={`w-8 h-8 mb-2 ${isGlobalDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                        <span className={`text-sm ${isGlobalDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Upload Story Image</span>
                      </>
                    )}
                  </button>
                </div>

                <div className="space-y-2">
                  <Label className={isGlobalDarkMode ? 'text-gray-200' : 'text-gray-700'}>Mission & Vision Image</Label>
                  <button
                    onClick={() => handleImageUpload('missionVisionImage')}
                    className={`w-full h-32 border-2 border-dashed rounded-lg transition-colors flex flex-col items-center justify-center ${
                      isGlobalDarkMode
                        ? 'border-zinc-700 hover:border-zinc-600 bg-zinc-800/50'
                        : 'border-gray-300 hover:border-gray-400 bg-gray-50'
                    }`}
                  >
                    {formData.missionVisionImage ? (
                      <img src={formData.missionVisionImage} alt="Mission Vision" className="h-full w-full object-cover rounded" />
                    ) : (
                      <>
                        <Upload className={`w-8 h-8 mb-2 ${isGlobalDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                        <span className={`text-sm ${isGlobalDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Upload Mission & Vision Image</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="space-y-3">
              <Label className={isGlobalDarkMode ? 'text-gray-200' : 'text-gray-700'}>
                Cover Image <span className="text-red-500">*</span>
              </Label>
              <button
                onClick={() => handleImageUpload('coverImage')}
                className={`w-full h-40 border-2 border-dashed rounded-lg transition-colors flex flex-col items-center justify-center ${
                  isGlobalDarkMode
                    ? 'border-zinc-700 hover:border-zinc-600 bg-zinc-800/50'
                    : 'border-gray-300 hover:border-gray-400 bg-gray-50'
                }`}
              >
                {formData.coverImage ? (
                  <img src={formData.coverImage} alt="Cover" className="h-full w-full object-cover rounded-lg" />
                ) : (
                  <>
                    <Upload className={`w-10 h-10 mb-2 ${isGlobalDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                    <span className={isGlobalDarkMode ? 'text-gray-400' : 'text-gray-600'}>Upload Cover Image</span>
                  </>
                )}
              </button>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-3">
                <Label className={isGlobalDarkMode ? 'text-gray-200' : 'text-gray-700'}>
                  Logo <span className="text-red-500">*</span>
                </Label>
                <button
                  onClick={() => handleImageUpload('logo')}
                  className={`w-full h-32 border-2 border-dashed rounded-lg transition-colors flex flex-col items-center justify-center ${
                    isGlobalDarkMode
                      ? 'border-zinc-700 hover:border-zinc-600 bg-zinc-800/50'
                      : 'border-gray-300 hover:border-gray-400 bg-gray-50'
                  }`}
                >
                  {formData.logo ? (
                    <img src={formData.logo} alt="Logo" className="h-full w-full object-contain p-2" />
                  ) : (
                    <>
                      <Upload className={`w-8 h-8 mb-2 ${isGlobalDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                      <span className={`text-sm ${isGlobalDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Upload Logo</span>
                    </>
                  )}
                </button>
                <textarea
                  placeholder="Logo details (optional)"
                  value={formData.logoDetails}
                  onChange={(e) => setFormData({ ...formData, logoDetails: e.target.value })}
                  rows={3}
                  className={`w-full px-3 py-2 border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    isGlobalDarkMode
                      ? 'bg-zinc-800 border-zinc-700 text-white placeholder-gray-400'
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                  }`}
                />
              </div>

              <div className="space-y-3">
                <Label className={isGlobalDarkMode ? 'text-gray-200' : 'text-gray-700'}>
                  Favicon <span className="text-red-500">*</span>
                </Label>
                <button
                  onClick={() => handleImageUpload('favicon')}
                  className={`w-full h-32 border-2 border-dashed rounded-lg transition-colors flex flex-col items-center justify-center ${
                    isGlobalDarkMode
                      ? 'border-zinc-700 hover:border-zinc-600 bg-zinc-800/50'
                      : 'border-gray-300 hover:border-gray-400 bg-gray-50'
                  }`}
                >
                  {formData.favicon ? (
                    <img src={formData.favicon} alt="Favicon" className="h-full w-full object-contain p-2" />
                  ) : (
                    <>
                      <Upload className={`w-8 h-8 mb-2 ${isGlobalDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                      <span className={`text-sm ${isGlobalDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Upload Favicon</span>
                    </>
                  )}
                </button>
                <textarea
                  placeholder="Icon details (optional)"
                  value={formData.iconDetails}
                  onChange={(e) => setFormData({ ...formData, iconDetails: e.target.value })}
                  rows={3}
                  className={`w-full px-3 py-2 border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    isGlobalDarkMode
                      ? 'bg-zinc-800 border-zinc-700 text-white placeholder-gray-400'
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                  }`}
                />
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-8">
            {/* Color System Section */}
            <div className="space-y-6">
              <div className="space-y-3">
                <Label className={isGlobalDarkMode ? 'text-gray-200' : 'text-gray-700'}>
                  Select a Color Preset
                </Label>
                {isLoadingColorPresets ? (
                  <LoadingSpinner size="sm" className="py-8" />
                ) : (
                  <Select
                    value={formData.selectedColorPreset?.id || ''}
                    onValueChange={(value) => {
                      const preset = colorPresets.find(p => p.id === value);
                      if (preset) {
                        setFormData({
                          ...formData,
                          selectedColorPreset: preset,
                          primaryColor: preset.colors[0]?.hex || '#3B82F6',
                          secondaryColor: preset.colors[1]?.hex || '',
                          accentColor: preset.colors[2]?.hex || '',
                          bgColor: preset.colors[3]?.hex || '#FFFFFF',
                          titleColor: preset.colors[4]?.hex || '#000000',
                          textColor: preset.colors[5]?.hex || ''
                        });
                      }
                    }}
                  >
                    <SelectTrigger className={isGlobalDarkMode ? 'bg-zinc-800 border-zinc-700 text-white' : ''}>
                      <SelectValue placeholder="Select a color preset" />
                    </SelectTrigger>
                    <SelectContent>
                      {colorPresets.map((preset) => (
                        <SelectItem key={preset.id} value={preset.id}>
                          {preset.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}

                {formData.selectedColorPreset && (
                  <div className="flex items-center gap-2 p-4 rounded-lg" style={{
                    backgroundColor: isGlobalDarkMode ? '#1f2937' : '#f9fafb',
                    border: `1px solid ${isGlobalDarkMode ? '#374151' : '#e5e7eb'}`
                  }}>
                    {formData.selectedColorPreset.colors.map((color, i) => (
                      <div
                        key={i}
                        className="w-10 h-10 rounded-full border-2 border-white shadow-md"
                        style={{ backgroundColor: color.hex }}
                        title={color.hex}
                      />
                    ))}
                  </div>
                )}
              </div>

              {formData.selectedColorPreset && (
                <div className="space-y-4">
                  <Label className={isGlobalDarkMode ? 'text-gray-200' : 'text-gray-700'}>Assign Colors</Label>

                  <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="primaryColor" className={`text-sm ${isGlobalDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      Primary <span className="text-red-500">*</span>
                    </Label>
                    <Select
                      value={formData.primaryColor}
                      onValueChange={(value) => setFormData({ ...formData, primaryColor: value })}
                    >
                      <SelectTrigger className={isGlobalDarkMode ? 'bg-zinc-800 border-zinc-700 text-white' : ''}>
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-full border" style={{ backgroundColor: formData.primaryColor }} />
                          <span>{formData.primaryColor}</span>
                        </div>
                      </SelectTrigger>
                      <SelectContent>
                        {formData.selectedColorPreset.colors.map((color, i) => (
                          <SelectItem key={i} value={color.hex}>
                            <div className="flex items-center gap-2">
                              <div className="w-6 h-6 rounded-full border" style={{ backgroundColor: color.hex }} />
                              <span>{color.hex}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="secondaryColor" className={`text-sm ${isGlobalDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      Secondary
                    </Label>
                    <Select
                      value={formData.secondaryColor || 'none'}
                      onValueChange={(value) => setFormData({ ...formData, secondaryColor: value === 'none' ? '' : value })}
                    >
                      <SelectTrigger className={isGlobalDarkMode ? 'bg-zinc-800 border-zinc-700 text-white' : ''}>
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-full border" style={{ backgroundColor: formData.secondaryColor || '#e5e7eb' }} />
                          <span>{formData.secondaryColor || 'None'}</span>
                        </div>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {formData.selectedColorPreset.colors.map((color, i) => (
                          <SelectItem key={i} value={color.hex}>
                            <div className="flex items-center gap-2">
                              <div className="w-6 h-6 rounded-full border" style={{ backgroundColor: color.hex }} />
                              <span>{color.hex}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="accentColor" className={`text-sm ${isGlobalDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      Accent
                    </Label>
                    <Select
                      value={formData.accentColor || 'none'}
                      onValueChange={(value) => setFormData({ ...formData, accentColor: value === 'none' ? '' : value })}
                    >
                      <SelectTrigger className={isGlobalDarkMode ? 'bg-zinc-800 border-zinc-700 text-white' : ''}>
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-full border" style={{ backgroundColor: formData.accentColor || '#e5e7eb' }} />
                          <span>{formData.accentColor || 'None'}</span>
                        </div>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {formData.selectedColorPreset.colors.map((color, i) => (
                          <SelectItem key={i} value={color.hex}>
                            <div className="flex items-center gap-2">
                              <div className="w-6 h-6 rounded-full border" style={{ backgroundColor: color.hex }} />
                              <span>{color.hex}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="bgColor" className={`text-sm ${isGlobalDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      Light <span className="text-red-500">*</span>
                    </Label>
                    <Select
                      value={formData.bgColor}
                      onValueChange={(value) => setFormData({ ...formData, bgColor: value })}
                    >
                      <SelectTrigger className={isGlobalDarkMode ? 'bg-zinc-800 border-zinc-700 text-white' : ''}>
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-full border" style={{ backgroundColor: formData.bgColor }} />
                          <span>{formData.bgColor}</span>
                        </div>
                      </SelectTrigger>
                      <SelectContent>
                        {formData.selectedColorPreset.colors.map((color, i) => (
                          <SelectItem key={i} value={color.hex}>
                            <div className="flex items-center gap-2">
                              <div className="w-6 h-6 rounded-full border" style={{ backgroundColor: color.hex }} />
                              <span>{color.hex}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="titleColor" className={`text-sm ${isGlobalDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      Dark <span className="text-red-500">*</span>
                    </Label>
                    <Select
                      value={formData.titleColor}
                      onValueChange={(value) => setFormData({ ...formData, titleColor: value })}
                    >
                      <SelectTrigger className={isGlobalDarkMode ? 'bg-zinc-800 border-zinc-700 text-white' : ''}>
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-full border" style={{ backgroundColor: formData.titleColor }} />
                          <span>{formData.titleColor}</span>
                        </div>
                      </SelectTrigger>
                      <SelectContent>
                        {formData.selectedColorPreset.colors.map((color, i) => (
                          <SelectItem key={i} value={color.hex}>
                            <div className="flex items-center gap-2">
                              <div className="w-6 h-6 rounded-full border" style={{ backgroundColor: color.hex }} />
                              <span>{color.hex}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="textColor" className={`text-sm ${isGlobalDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      Text
                    </Label>
                    <Select
                      value={formData.textColor || 'none'}
                      onValueChange={(value) => setFormData({ ...formData, textColor: value === 'none' ? '' : value })}
                    >
                      <SelectTrigger className={isGlobalDarkMode ? 'bg-zinc-800 border-zinc-700 text-white' : ''}>
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-full border" style={{ backgroundColor: formData.textColor || '#e5e7eb' }} />
                          <span>{formData.textColor || 'None'}</span>
                        </div>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {formData.selectedColorPreset.colors.map((color, i) => (
                          <SelectItem key={i} value={color.hex}>
                            <div className="flex items-center gap-2">
                              <div className="w-6 h-6 rounded-full border" style={{ backgroundColor: color.hex }} />
                              <span>{color.hex}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            )}
            </div>

            {/* Typography System Section */}
            <div className="space-y-6 pt-6 border-t" style={{ borderColor: isGlobalDarkMode ? '#374151' : '#e5e7eb' }}>
              <div className="space-y-3">
                <Label className={isGlobalDarkMode ? 'text-gray-200' : 'text-gray-700'}>
                  Select Typography Preset
                </Label>
                {isLoadingTypographyPresets ? (
                  <LoadingSpinner size="sm" className="py-8" />
                ) : (
                  <Select
                    value={formData.selectedTypographyPreset?.id || ''}
                    onValueChange={(value) => {
                      const preset = typographyPresets.find(p => p.id === value);
                      if (preset) {
                        const scale = calculateTypeScale(
                          preset.min_font_size,
                          preset.max_font_size,
                          preset.min_scale_ratio,
                          preset.max_scale_ratio,
                          preset.base_scale_index
                        );

                        const h1 = scale.find(s => s.name === '4xl')?.maxSize || 48;
                        const h2 = scale.find(s => s.name === '3xl')?.maxSize || 36;
                        const h3 = scale.find(s => s.name === '2xl')?.maxSize || 30;
                        const h4 = scale.find(s => s.name === 'xl')?.maxSize || 24;
                        const h5 = scale.find(s => s.name === 'l')?.maxSize || 20;
                        const body = scale.find(s => s.name === 'm')?.maxSize || 16;

                        setFormData({
                          ...formData,
                          selectedTypographyPreset: preset,
                          headingFont: preset.heading_font_family,
                          bodyFont: preset.body_font_family,
                          h1Size: h1.toString(),
                          h2Size: h2.toString(),
                          h3Size: h3.toString(),
                          h4Size: h4.toString(),
                          h5Size: h5.toString(),
                          bodySize: body.toString()
                        });
                      }
                    }}
                  >
                    <SelectTrigger className={isGlobalDarkMode ? 'bg-zinc-800 border-zinc-700 text-white' : ''}>
                      <SelectValue placeholder="Select a typography preset" />
                    </SelectTrigger>
                    <SelectContent>
                      {typographyPresets.map((preset) => (
                        <SelectItem key={preset.id} value={preset.id}>
                          <div className="flex flex-col">
                            <span className="font-medium">{preset.name}</span>
                            <span className="text-xs text-gray-500">{preset.heading_font_family} / {preset.body_font_family}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              {formData.selectedTypographyPreset && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className={isGlobalDarkMode ? 'text-gray-200' : 'text-gray-700'}>
                        Heading Font <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        value={formData.headingFont}
                        onChange={(e) => setFormData({ ...formData, headingFont: e.target.value })}
                        placeholder="Inter"
                        className={isGlobalDarkMode ? 'bg-zinc-800 border-zinc-700 text-white' : ''}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className={isGlobalDarkMode ? 'text-gray-200' : 'text-gray-700'}>
                        Body Font <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        value={formData.bodyFont}
                        onChange={(e) => setFormData({ ...formData, bodyFont: e.target.value })}
                        placeholder="Inter"
                        className={isGlobalDarkMode ? 'bg-zinc-800 border-zinc-700 text-white' : ''}
                      />
                    </div>
                  </div>

                  <div>
                    <Label className={`mb-3 block ${isGlobalDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                      Assign Type Scale (px)
                    </Label>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <Label htmlFor="h1" className={`text-xs ${isGlobalDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>H1</Label>
                        <Input
                          id="h1"
                          type="number"
                          value={formData.h1Size}
                          onChange={(e) => setFormData({ ...formData, h1Size: e.target.value })}
                          className={isGlobalDarkMode ? 'bg-zinc-800 border-zinc-700 text-white' : ''}
                        />
                      </div>
                      <div>
                        <Label htmlFor="h2" className={`text-xs ${isGlobalDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>H2</Label>
                        <Input
                          id="h2"
                          type="number"
                          value={formData.h2Size}
                          onChange={(e) => setFormData({ ...formData, h2Size: e.target.value })}
                          className={isGlobalDarkMode ? 'bg-zinc-800 border-zinc-700 text-white' : ''}
                        />
                      </div>
                      <div>
                        <Label htmlFor="h3" className={`text-xs ${isGlobalDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>H3</Label>
                        <Input
                          id="h3"
                          type="number"
                          value={formData.h3Size}
                          onChange={(e) => setFormData({ ...formData, h3Size: e.target.value })}
                          className={isGlobalDarkMode ? 'bg-zinc-800 border-zinc-700 text-white' : ''}
                        />
                      </div>
                      <div>
                        <Label htmlFor="h4" className={`text-xs ${isGlobalDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>H4</Label>
                        <Input
                          id="h4"
                          type="number"
                          value={formData.h4Size}
                          onChange={(e) => setFormData({ ...formData, h4Size: e.target.value })}
                          className={isGlobalDarkMode ? 'bg-zinc-800 border-zinc-700 text-white' : ''}
                        />
                      </div>
                      <div>
                        <Label htmlFor="h5" className={`text-xs ${isGlobalDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>H5</Label>
                        <Input
                          id="h5"
                          type="number"
                          value={formData.h5Size}
                          onChange={(e) => setFormData({ ...formData, h5Size: e.target.value })}
                          className={isGlobalDarkMode ? 'bg-zinc-800 border-zinc-700 text-white' : ''}
                        />
                      </div>
                      <div>
                        <Label htmlFor="body" className={`text-xs ${isGlobalDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Body</Label>
                        <Input
                          id="body"
                          type="number"
                          value={formData.bodySize}
                          onChange={(e) => setFormData({ ...formData, bodySize: e.target.value })}
                          className={isGlobalDarkMode ? 'bg-zinc-800 border-zinc-700 text-white' : ''}
                        />
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div className="space-y-3">
              <Label className={isGlobalDarkMode ? 'text-gray-200' : 'text-gray-700'}>
                Brand Moods <span className="text-gray-400 text-sm">(Select multiple)</span>
              </Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={`w-full justify-between ${
                      isGlobalDarkMode ? 'bg-zinc-800 border-zinc-700 text-white hover:bg-zinc-700' : ''
                    }`}
                  >
                    <span className={formData.brandMoods.length === 0 ? 'text-gray-400' : ''}>
                      {formData.brandMoods.length === 0
                        ? 'Select moods'
                        : `${formData.brandMoods.length} mood${formData.brandMoods.length > 1 ? 's' : ''} selected`}
                    </span>
                    <ChevronDown className="w-4 h-4 ml-2 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className={`w-full p-4 ${isGlobalDarkMode ? 'bg-zinc-800 border-zinc-700' : ''}`}>
                  <div className="grid grid-cols-2 gap-3">
                    {brandMoods.map((mood) => (
                      <div key={mood} className="flex items-center space-x-2">
                        <Checkbox
                          id={`mood-${mood}`}
                          checked={formData.brandMoods.includes(mood)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setFormData({
                                ...formData,
                                brandMoods: [...formData.brandMoods, mood]
                              });
                            } else {
                              setFormData({
                                ...formData,
                                brandMoods: formData.brandMoods.filter((m) => m !== mood)
                              });
                            }
                          }}
                        />
                        <label
                          htmlFor={`mood-${mood}`}
                          className={`text-sm cursor-pointer ${
                            isGlobalDarkMode ? 'text-gray-300' : 'text-gray-700'
                          }`}
                        >
                          {mood}
                        </label>
                      </div>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
              {formData.brandMoods.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {formData.brandMoods.map((mood) => (
                    <div
                      key={mood}
                      className={`px-3 py-1 rounded-full text-sm flex items-center gap-2 ${
                        isGlobalDarkMode
                          ? 'bg-blue-600 text-white'
                          : 'bg-blue-500 text-white'
                      }`}
                    >
                      {mood}
                      <button
                        type="button"
                        onClick={() => {
                          setFormData({
                            ...formData,
                            brandMoods: formData.brandMoods.filter((m) => m !== mood)
                          });
                        }}
                        className="hover:text-red-200"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className={isGlobalDarkMode ? 'text-gray-200' : 'text-gray-700'}>
                  Moodboard Images <span className="text-gray-400 text-sm">(5-10 images)</span>
                </Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setCurrentImageField('moodboard' as ImageField);
                    setShowImageModal(true);
                  }}
                  disabled={formData.moodboardImages.length >= 10}
                  className="h-8"
                >
                  <Upload className="w-4 h-4 mr-1" />
                  Add Image
                </Button>
              </div>
              <div className="grid grid-cols-3 gap-3">
                {formData.moodboardImages.map((image, index) => (
                  <div key={index} className="relative aspect-square group">
                    <img
                      src={image}
                      alt={`Moodboard ${index + 1}`}
                      className="w-full h-full object-cover rounded-lg"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setFormData({
                          ...formData,
                          moodboardImages: formData.moodboardImages.filter((_, i) => i !== index)
                        });
                      }}
                      className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
                {formData.moodboardImages.length < 10 && (
                  <button
                    type="button"
                    onClick={() => {
                      setCurrentImageField('moodboard' as ImageField);
                      setShowImageModal(true);
                    }}
                    className={`aspect-square border-2 border-dashed rounded-lg flex items-center justify-center transition-colors ${
                      isGlobalDarkMode
                        ? 'border-zinc-700 hover:border-zinc-600 bg-zinc-800/50'
                        : 'border-gray-300 hover:border-gray-400 bg-gray-50'
                    }`}
                  >
                    <Plus className={`w-8 h-8 ${isGlobalDarkMode ? 'text-gray-600' : 'text-gray-400'}`} />
                  </button>
                )}
              </div>
              {formData.moodboardImages.length < 5 && (
                <p className="text-xs text-amber-500">Please upload at least 5 images</p>
              )}
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="phone" className={isGlobalDarkMode ? 'text-gray-200' : 'text-gray-700'}>
                Business Phone
              </Label>
              <Input
                id="phone"
                type="tel"
                value={formData.contactPhone}
                onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })}
                placeholder="+1 (555) 123-4567"
                className={isGlobalDarkMode ? 'bg-zinc-800 border-zinc-700 text-white' : ''}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className={isGlobalDarkMode ? 'text-gray-200' : 'text-gray-700'}>
                Business Email
              </Label>
              <Input
                id="email"
                type="email"
                value={formData.contactEmail}
                onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
                placeholder="contact@yourbrand.com"
                className={isGlobalDarkMode ? 'bg-zinc-800 border-zinc-700 text-white' : ''}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="address" className={isGlobalDarkMode ? 'text-gray-200' : 'text-gray-700'}>
                Business Address <span className="text-gray-400 text-sm">(optional)</span>
              </Label>
              <Input
                id="address"
                value={formData.contactAddress}
                onChange={(e) => setFormData({ ...formData, contactAddress: e.target.value })}
                placeholder="123 Main St, City, Country"
                className={isGlobalDarkMode ? 'bg-zinc-800 border-zinc-700 text-white' : ''}
              />
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className={isGlobalDarkMode ? 'text-gray-200' : 'text-gray-700'}>Social Media Links</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addSocialLink}
                  className="h-8"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add
                </Button>
              </div>

              {formData.socialLinks.map((link, index) => (
                <div key={index} className="flex gap-2">
                  <Select
                    value={link.platform}
                    onValueChange={(value) => updateSocialLink(index, 'platform', value)}
                  >
                    <SelectTrigger className={`w-32 ${isGlobalDarkMode ? 'bg-zinc-800 border-zinc-700 text-white' : ''}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="facebook">Facebook</SelectItem>
                      <SelectItem value="twitter">Twitter</SelectItem>
                      <SelectItem value="instagram">Instagram</SelectItem>
                      <SelectItem value="linkedin">LinkedIn</SelectItem>
                      <SelectItem value="youtube">YouTube</SelectItem>
                      <SelectItem value="tiktok">TikTok</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    value={link.url}
                    onChange={(e) => updateSocialLink(index, 'url', e.target.value)}
                    placeholder="https://..."
                    className={`flex-1 ${isGlobalDarkMode ? 'bg-zinc-800 border-zinc-700 text-white' : ''}`}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeSocialLink(index)}
                    className="h-10 w-10 p-0"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className={`min-h-screen ${isGlobalDarkMode ? 'bg-zinc-900' : 'bg-gray-50'}`}>
      <div className="flex h-screen">
        <div className="w-full lg:w-1/2 flex flex-col">
          <div className="flex-1 overflow-y-auto">
            <div className="max-w-2xl mx-auto px-6 py-12">
              <div className="mb-8">
                <div className="flex items-center mb-6">
                  {steps.map((step, index) => (
                    <React.Fragment key={step.id}>
                      <div
                        className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold transition-colors flex-shrink-0 ${
                          index === currentStep
                            ? 'bg-blue-500 text-white'
                            : index < currentStep
                            ? 'bg-green-500 text-white'
                            : isGlobalDarkMode
                            ? 'bg-zinc-800 text-gray-400'
                            : 'bg-gray-200 text-gray-500'
                        }`}
                      >
                        {index < currentStep ? <Check className="w-5 h-5" /> : index + 1}
                      </div>
                      {index < steps.length - 1 && (
                        <div
                          className={`flex-1 h-1 mx-2 transition-colors ${
                            index < currentStep
                              ? 'bg-green-500'
                              : isGlobalDarkMode
                              ? 'bg-zinc-800'
                              : 'bg-gray-200'
                          }`}
                        />
                      )}
                    </React.Fragment>
                  ))}
                </div>

                <h1 className={`text-3xl font-bold ${isGlobalDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {steps[currentStep].title}
                </h1>
              </div>

              <AnimatePresence mode="wait">
                <motion.div
                  key={currentStep}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  {renderStepContent()}
                </motion.div>
              </AnimatePresence>
            </div>
          </div>

          <div className={`border-t p-6 ${isGlobalDarkMode ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-gray-200'}`}>
            <div className="max-w-2xl mx-auto flex justify-between">
              {currentStep === 0 ? (
                <Button
                  onClick={() => navigate('/brand')}
                  variant="outline"
                  className="px-6"
                >
                  <ChevronLeft className="w-5 h-5 mr-2" />
                  Back
                </Button>
              ) : (
                <Button
                  onClick={handlePrev}
                  variant="outline"
                  className="px-6"
                >
                  <ChevronLeft className="w-5 h-5 mr-2" />
                  Previous
                </Button>
              )}

              {currentStep === steps.length - 1 ? (
                <Button
                  onClick={handleGenerate}
                  disabled={!canProceed()}
                  className="px-8 bg-blue-600 hover:bg-blue-700"
                >
                  Generate Brand Guidelines
                </Button>
              ) : (
                <Button
                  onClick={handleNext}
                  disabled={!canProceed()}
                  className="px-6 bg-blue-600 hover:bg-blue-700"
                >
                  Next
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
              )}
            </div>
          </div>
        </div>

        <div className="hidden lg:block lg:w-1/2 relative">
          <img
            src="/agencyplus-onboarding.webp"
            alt="Onboarding visual"
            className="w-full h-full object-cover"
          />
        </div>
      </div>

      <ImageUploadModal
        isOpen={showImageModal}
        onClose={() => {
          setShowImageModal(false);
          setCurrentImageField(null);
        }}
        onImageSelect={handleImageSelect}
        isGlobalDarkMode={isGlobalDarkMode}
        title="Upload Image"
        acceptedFormats={['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg']}
        maxFileSize={10}
        showUnsplash={true}
      />
    </div>
  );
};
